import { useSearchParams, Link } from "react-router-dom";
import { useState, useCallback, Fragment } from "react";
import { useUser } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { usePlan } from "../App";
import { getProbeHistory } from "../lib/api";
import { ProbeRun } from "../types";
import ResultsTable from "../components/ResultsTable";
import RoutingInsight from "../components/RoutingInsight";
import { Bone } from "../components/Skeleton";

function statusLabel(s: string) {
  if (s === "complete") return "Successful";
  if (s === "error") return "Failed";
  if (s === "running") return "Running";
  return s;
}

function statusColor(s: string) {
  if (s === "complete") return "#39D98A";
  if (s === "error") return "#FF5C5C";
  return "#FDDD48";
}

function statusBg(s: string) {
  if (s === "complete") return "rgba(57,217,138,0.1)";
  if (s === "error") return "rgba(255,92,92,0.1)";
  return "rgba(253,221,72,0.1)";
}

export default function Dashboard() {
  const { user, isLoaded } = useUser();
  const [searchParams] = useSearchParams();
  const upgraded = searchParams.get("upgraded") === "true";
  const plan = usePlan();

  if (!isLoaded) return (
    <div style={{ minHeight: "100vh" }}>
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "40px 24px" }}>
        <Bone w={40} h={13} style={{ marginBottom: 32 }} />
        <Bone w={160} h={26} style={{ marginBottom: 8 }} />
        <Bone w={240} h={14} style={{ marginBottom: 32 }} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 16, marginBottom: 40 }}>
          <div className="glass" style={{ padding: "20px 24px" }}><Bone w={120} h={15} style={{ marginBottom: 8 }} /><Bone w="80%" h={13} /></div>
          <div className="glass" style={{ padding: "20px 24px" }}><Bone w={100} h={15} style={{ marginBottom: 8 }} /><Bone w="90%" h={13} /></div>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh" }}>
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "40px 24px" }}>
        <Link to="/" style={backLink}>← Back</Link>
        {upgraded && (
          <div style={{
            marginBottom: 24, padding: "16px 20px",
            background: "rgba(57,217,138,0.08)",
            border: "1px solid rgba(57,217,138,0.3)",
            borderRadius: 3, color: "#39D98A", fontWeight: 600,
          }}>
            Welcome to Pro! Your subscription is now active.
          </div>
        )}

        <h1 style={{ margin: "0 0 8px", fontSize: 26, fontWeight: 700, color: "#EFF2F5" }}>Dashboard</h1>
        <p style={{ color: "#98A1AB", margin: "0 0 32px" }}>
          {user?.emailAddresses[0]?.emailAddress} · {plan.charAt(0).toUpperCase() + plan.slice(1)} plan
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 16, marginBottom: 40 }}>
          <DashCard to="/" title="Run a Probe" description="Test any HTTP endpoint from 10 global regions" />
          <DashCard to="/dashboard/monitors" title="Monitors" description="Track uptime and global latency for your endpoints" />
        </div>

        <RecentRunsTable />
      </div>
    </div>
  );
}

function RecentRunsTable() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["history", 0],
    queryFn: () => getProbeHistory(20, 0),
  });

  const runs: ProbeRun[] = data?.runs ?? [];
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const handleRowClick = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#EFF2F5" }}>Recent Runs</h2>
        <Link to="/dashboard/history" style={{ fontSize: 13, color: "#98A1AB", textDecoration: "none" }}>
          View full history →
        </Link>
      </div>

      <div className="glass" style={{ padding: 24 }}>
        {isLoading ? (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                <th style={thStyle("left")}>Endpoint</th>
                <th style={thStyle("left")}>Started</th>
                <th style={thStyle("right")}>Status</th>
                <th style={thStyle("right")}>P50</th>
                <th style={thStyle("right")}>P95</th>
                <th style={thStyle("right")}>Regions</th>
              </tr>
            </thead>
            <tbody>
              {[190, 160, 220, 170, 200].map((urlW, i) => (
                <tr key={i} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                  <td style={{ padding: "10px 12px" }}><Bone w={urlW} h={13} /></td>
                  <td style={{ padding: "10px 12px" }}><Bone w={88} h={13} /></td>
                  <td style={{ padding: "10px 12px", textAlign: "right" }}><Bone w={64} h={20} style={{ marginLeft: "auto" }} /></td>
                  <td style={{ padding: "10px 12px", textAlign: "right" }}><Bone w={44} h={13} style={{ marginLeft: "auto" }} /></td>
                  <td style={{ padding: "10px 12px", textAlign: "right" }}><Bone w={44} h={13} style={{ marginLeft: "auto" }} /></td>
                  <td style={{ padding: "10px 12px", textAlign: "right" }}><Bone w={28} h={13} style={{ marginLeft: "auto" }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : isError ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#98A1AB" }}>
            <p style={{ fontSize: 14, color: "#EFF2F5", margin: "0 0 6px" }}>Service temporarily unavailable</p>
            <p style={{ fontSize: 13, margin: 0 }}>We're having trouble connecting to our data service. Please try refreshing the page.</p>
          </div>
        ) : runs.length === 0 ? (
          <p style={{ textAlign: "center", color: "#98A1AB", padding: "40px 0", margin: 0 }}>
            No runs yet. <Link to="/" style={{ color: "#FF2566" }}>Run your first probe →</Link>
          </p>
        ) : (
          <>
            <p style={{ margin: "0 0 14px", fontSize: 12, color: "#98A1AB" }}>
              Click any row to see per-region breakdown
            </p>
            <table className="tbl-hover" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                  <th style={thStyle("left")}>Endpoint</th>
                  <th style={thStyle("left")}>Started</th>
                  <th style={thStyle("right")}>Status</th>
                  <th style={thStyle("right")}>P50</th>
                  <th style={thStyle("right")}>P95</th>
                  <th style={thStyle("right")}>Regions</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => {
                  const isExpanded = expandedId === r.run_id;
                  return (
                    <Fragment key={r.run_id}>
                      <tr
                        onClick={() => handleRowClick(r.run_id)}
                        style={{
                          borderBottom: isExpanded ? "none" : "1px solid rgba(255,255,255,0.05)",
                          cursor: "pointer",
                          background: isExpanded ? "rgba(255,255,255,0.03)" : "transparent",
                          transition: "background 0.15s",
                        }}
                      >
                        <td style={{ padding: "10px 12px", color: "#EFF2F5", maxWidth: 220 }}>
                          <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.url}>
                            {r.url.replace(/^https?:\/\//, "")}
                          </span>
                        </td>
                        <td style={{ padding: "10px 12px", color: "#98A1AB", whiteSpace: "nowrap" }}>
                          {new Date(r.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "right" }}>
                          <span style={{ color: statusColor(r.status), fontWeight: 600, fontSize: 12, padding: "2px 8px", borderRadius: 3, background: statusBg(r.status) }}>
                            {statusLabel(r.status)}
                          </span>
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "right", color: r.percentiles?.p50 != null ? "#39D98A" : "#98A1AB", fontWeight: r.percentiles?.p50 != null ? 700 : 400 }}>
                          {r.percentiles?.p50 != null ? `${r.percentiles.p50}ms` : "—"}
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "right", color: r.percentiles?.p95 != null ? "#FDDD48" : "#98A1AB" }}>
                          {r.percentiles?.p95 != null ? `${r.percentiles.p95}ms` : "—"}
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "right", color: "#98A1AB" }}>
                          {r.regions_done}/{r.region_count}
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                          <td colSpan={6} style={{ padding: "0 12px 16px" }}>
                            <div style={{ marginTop: 8, padding: 16, background: "rgba(18,18,18,0.5)", borderRadius: 3, border: "1px solid rgba(255,255,255,0.07)" }}>
                              <ResultsTable results={r.results ?? []} regionCount={r.region_count} regionsDone={r.regions_done} />
                              <RoutingInsight results={r.results ?? []} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

function DashCard({ to, title, description, cta = false }: { to: string; title: string; description: string; cta?: boolean }) {
  return (
    <Link
      to={to}
      className="glass"
      style={{
        display: "block", padding: "20px 24px", textDecoration: "none", transition: "background 0.15s",
        ...(cta ? { background: "rgba(255,37,102,0.08)", border: "1px solid rgba(255,37,102,0.25)" } : {}),
      }}
    >
      <p style={{ margin: "0 0 6px", fontWeight: 700, fontSize: 15, color: cta ? "#FF2566" : "#EFF2F5" }}>{title}</p>
      <p style={{ margin: 0, fontSize: 13, color: "#98A1AB", lineHeight: 1.5 }}>{description}</p>
    </Link>
  );
}

function thStyle(align: "left" | "right"): React.CSSProperties {
  return {
    textAlign: align, padding: "8px 12px",
    color: "#98A1AB", fontWeight: 700,
    fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em",
  };
}

const backLink: React.CSSProperties = { display: "inline-block", marginBottom: 20, fontSize: 13, color: "#98A1AB", textDecoration: "none" };
